import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { updateData } from 'services/CommonServices';
import { alertOptions } from 'utils/Config';
import { MASTER_ENDPOINT } from 'utils/Constant';

interface CustomUploadFileDialogProps {
  open: boolean;
  onClose: () => void;
  candidateId: string;
  jobId: string;
  onUploadSuccess: () => void;
  attachmentTypes: any[];
}

const CustomUploadFileDialog: React.FC<CustomUploadFileDialogProps> = ({
  open,
  onClose,
  candidateId,
  jobId,
  onUploadSuccess,
  attachmentTypes,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/msword': ['.doc'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'image/*': ['.jpeg', '.jpg', '.png'],
    },
    multiple: false,
    onDrop,
  });

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = 1;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const handleUpload = async () => {
    if (!file || !selectedType) {
      toast.error('Please select a file and attachment type', alertOptions);
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = `${MASTER_ENDPOINT.Candidates}/attachments/${candidateId}/${selectedType}/${jobId}`;

      await updateData('', formData, endpoint, false);
      toast.success('File uploaded successfully', alertOptions);
      onUploadSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Upload failed', alertOptions);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setSelectedType('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="upload-file-dialog"
      fullWidth
      maxWidth="xs">
      <Box className="dialog-header">
        <Box className="header-text">
          <Typography className="title">Upload File</Typography>
          <Typography className="subtitle">
            Select and upload the files from your device
          </Typography>
        </Box>
        <IconButton onClick={handleClose} className="close-btn" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className="dialog-content">
        <Box {...getRootProps()} className="dropzone-area">
          <input {...getInputProps()} />
          {file ? (
            <Box className="file-preview">
              <Box className="file-icon-box">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="1">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <Box className="pdf-badge">PDF</Box>
              </Box>
              <Typography className="file-name">{file.name}</Typography>
              <Box className="file-status">
                <Typography component="span" className="file-size">
                  {formatFileSize(file.size)} •
                </Typography>
                <CheckCircleIcon className="status-icon" />
                <Typography component="span" className="status-text">
                  Completed
                </Typography>
              </Box>
              <Typography className="remove-link" onClick={handleRemove}>
                Remove
              </Typography>
            </Box>
          ) : (
            <Box className="upload-placeholder">
              <Box
                component="img"
                src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23dfe1e6' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'><path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'></path><polyline points='14 2 14 8 20 8'></polyline></svg>"
                className="placeholder-icon"
              />
              <Typography>Click to upload or drag and drop</Typography>
            </Box>
          )}
        </Box>

        <Box className="type-select-wrapper">
          <Select
            fullWidth
            displayEmpty
            value={selectedType}
            onChange={(e) => setSelectedType(String(e.target.value))}
            variant="outlined"
            size="small"
            className="type-select"
            renderValue={(selected) => {
              if (!selected) {
                return <span className="placeholder">Attachment Type</span>;
              }
              const type = attachmentTypes.find(
                (t) => String(t.id) === String(selected),
              );
              return type ? type.name : selected;
            }}>
            {attachmentTypes.map((type) => (
              <MenuItem
                key={type.id}
                value={type.id}
                className="type-select-menu-item">
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box className="dialog-footer">
        <Button onClick={handleClose} className="cancel-btn-upload">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!file || !selectedType || isUploading}
          className="upload-btn-upload">
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CustomUploadFileDialog;
